import { useMemo, useState } from "react";
import { IconBuilding, IconLinkOff, IconSearch } from "@tabler/icons-react";
import { AdminOrganization } from "../../utils/types";

type UserOrgLinkPickerProps = {
  organizations: AdminOrganization[];
  selectedOrgIds: number[];
  onChange: (orgIds: number[]) => void;
  disabled?: boolean;
};

export function UserOrgLinkPicker({
  organizations,
  selectedOrgIds,
  onChange,
  disabled = false,
}: UserOrgLinkPickerProps) {
  const [search, setSearch] = useState("");

  const selectedOrgs = useMemo(
    () =>
      selectedOrgIds
        .map((orgId) => organizations.find((org) => org.id === orgId))
        .filter((org): org is AdminOrganization => org !== undefined),
    [organizations, selectedOrgIds],
  );

  const filteredOrganizations = useMemo(() => {
    const query = search.trim().toLowerCase();
    const sorted = [...organizations].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    if (!query) return sorted;
    return sorted.filter((org) => {
      const haystack = [org.name, String(org.id), org.support_email ?? ""]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [organizations, search]);

  const toggleOrg = (orgId: number) => {
    if (disabled) return;
    if (selectedOrgIds.includes(orgId)) {
      onChange(selectedOrgIds.filter((id) => id !== orgId));
      return;
    }
    onChange([...selectedOrgIds, orgId]);
  };

  const optionClassName = (isSelected: boolean) =>
    `flex w-full items-center gap-3 px-4 py-3 text-left transition ${
      isSelected ? "bg-indigo-50" : "bg-white hover:bg-slate-50"
    } ${disabled ? "cursor-not-allowed opacity-60" : ""}`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-800">
            Linked organizations
          </p>
          <p className="text-xs text-slate-500">
            Check one or more organizations for this user.
          </p>
        </div>
        {selectedOrgIds.length > 0 ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange([])}
            className="shrink-0 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Clear all
          </button>
        ) : null}
      </div>

      <div
        className={`rounded-xl border px-4 py-3 ${
          selectedOrgs.length > 0
            ? "border-indigo-200 bg-indigo-50/60"
            : "border-slate-200 bg-slate-50"
        }`}
      >
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Current selection
        </p>
        <div className="mt-1 flex items-start gap-2">
          {selectedOrgs.length > 0 ? (
            <IconBuilding size={18} className="mt-0.5 shrink-0 text-indigo-600" />
          ) : (
            <IconLinkOff size={18} className="mt-0.5 shrink-0 text-slate-500" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              {selectedOrgs.length === 0
                ? "No organizations"
                : `${selectedOrgs.length} organization${
                    selectedOrgs.length === 1 ? "" : "s"
                  } selected`}
            </p>
            <p className="text-xs text-slate-500">
              {selectedOrgs.length === 0
                ? "User is not linked to any organization"
                : selectedOrgs.map((org) => org.name).join(", ")}
            </p>
          </div>
        </div>
      </div>

      <label className="sr-only" htmlFor="user-org-link-search">
        Search organizations
      </label>
      <div className="relative">
        <IconSearch
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          id="user-org-link-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search organizations by name or ID…"
          autoComplete="off"
          disabled={disabled}
          className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-50"
        />
      </div>

      <div
        role="group"
        aria-label="Linked organizations"
        className="overflow-hidden rounded-xl border border-slate-200 bg-white"
      >
        <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
          {filteredOrganizations.map((org) => {
            const isSelected = selectedOrgIds.includes(org.id);
            const checkboxId = `user-org-link-${org.id}`;
            return (
              <label
                key={org.id}
                htmlFor={checkboxId}
                className={optionClassName(isSelected)}
              >
                <input
                  id={checkboxId}
                  type="checkbox"
                  checked={isSelected}
                  disabled={disabled}
                  onChange={() => toggleOrg(org.id)}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isSelected
                      ? "bg-indigo-100 text-indigo-700"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  <IconBuilding size={18} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-900">
                    {org.name}
                  </span>
                  <span className="block text-xs text-slate-500">
                    ID {org.id}
                    {org.support_email ? ` · ${org.support_email}` : ""}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {search.trim() && filteredOrganizations.length === 0 ? (
        <p className="text-xs text-slate-500">
          No organizations match &ldquo;{search.trim()}&rdquo;.
        </p>
      ) : null}
    </div>
  );
}
